import {
	baseUrl,
	cleanLocationTags,
	sumValues
} from '@/core';
import {
	getTripsRequest,
	getTransporterOrdersRequest,
	updateTripOrdersRequest,
	updateTransporterOrderRequest,
	sendTripRequest,
	scheduleTripOnEdiRequest,
	deleteTripsRequest,
} from '../requests';
import deepCopy from 'deepcopy';
import searchFilterActions from './searchFilter/tripSearch.actions.js';
import { moveOrderToDifferentTrip } from './actions';
import rdbClient from 'rdb-client';

function setValue({ commit }, { value, key }) {
	commit('setValue', { value, key });
}

function cleanSearchFilter(searchFilter) {
	return {
		etd: searchFilter.etd,
		fromLocation: Array.isArray(searchFilter.fromLocation) ? cleanLocationTags(searchFilter.fromLocation) : null,
		toLocation: Array.isArray(searchFilter.toLocation) ? cleanLocationTags(searchFilter.toLocation) : null,
		tripType: Array.isArray(searchFilter.tripType) ? searchFilter.tripType.map (x => x.id) : [],
		cargoCarrier: Array.isArray(searchFilter.cargoCarrier) ? searchFilter.cargoCarrier.map(x => ({ id: x.id, description: x.description, type: x.type, op: x.op })) : [],
		status: searchFilter.status || [],
	};
}


// eslint-disable-next-line no-unused-vars
async function scheduleTripOnEdi({ commit, state: { tripDetails } }, tripId) {
	try {
		await scheduleTripOnEdiRequest(tripId);
		commit('ediSent', tripDetails.id);
	} catch (err) {
		throw new Error(err.response.data);
	}
}

async function fetchTrips({ commit, state: { searchFilter, tripDetails } }) {
	commit('setValue', { value: true, key: 'tripsLoading' });
	try {
		let searchTrips = searchFilter.tripType.length === 0 || searchFilter.tripType.some(x => x.id !== 'external');
		let serachExternalTrips = searchFilter.tripType.length === 0 || searchFilter.tripType.some(x => x.id === 'external');

		let trips = searchTrips ? await getTripsRequest(cleanSearchFilter(searchFilter)) : [];
		let externalTrips = serachExternalTrips ? await getTransporterOrdersRequest({ etdFrom: searchFilter.etd.from, etdTo: searchFilter.etd.to }) : [];
		commit('setTrips', { trips, externalTrips });
		if (tripDetails) commit('setTripDetails', tripDetails.id);
	} catch (err) {
		throw new Error('failedToFetch');
	} finally {
		commit('setValue', { value: false, key: 'tripsLoading' });
	}
}

function loadSavedSearch({ commit }, savedSearch) {
	if (savedSearch) commit('loadSavedSearch', savedSearch);
}

function setTripDetails({ commit }, id) {
	commit('setTripDetails', id);
}

async function removeOrdersFromTrip({ state, commit }, orders) {
	if (!orders || !state.tripDetails || !state.tripDetails.orders) return;
	let trip = state.tripDetails;

	let subOrderUpdates = [];
	orders.forEach(order => {
		subOrderUpdates.push({
			removedOrderId: order.id
		});
	});

	let tripClone = deepCopy(trip);

	// Remove orders
	let cleanOrderList = trip.orders.filter(order => orders.findIndex( x => x.id === order.id) === -1);
	trip.volume = sumValues(cleanOrderList, 'volume');
	trip.grossWeight = sumValues(cleanOrderList, 'grossWeight');
	trip.loadingMeters = sumValues(cleanOrderList, 'loadingMeters');

	try {
		let patch = rdbClient.createPatch(tripClone, trip);
		let response = await updateTripOrdersRequest(trip.id, { tripPatches: patch, subOrderUpdates });
		commit('setTrip', response);
	} catch (err) {
		throw new Error('failedToRemoveOrderTrip');
	}
}

async function removeOrdersFromTransporter({ state, commit }, orders) {
	if (!orders || !state.tripDetails || !state.tripDetails.orders) return;
	try {
		let removedIds = orders.map(({ orderId, id }) => ({ orderId, id }));
		let response = await updateTransporterOrderRequest(state.tripDetails.id, { removedOrders: removedIds });
		commit('setTrip', response);
	} catch (err) {
		throw new Error('failedRemoveOrderTransporter');
	}
}

async function getLoadingList(_, { tripIds, landscape }) {
	let base = baseUrl();
	let locale = localStorage.getItem('locale') || 'no';
	let landscapeOrientation = landscape ? '/landscape' : '';
	if (Array.isArray(tripIds) && tripIds.length > 1) {
		let form = document.createElement('form');
		form.target = '_blank';
		form.method = 'POST';
		form.action = `${base}/documents/loadingList/${locale}/merged${landscapeOrientation}`;
		form.style.display = 'none';

		let input = document.createElement('input');
		input.name = 'ids';
		input.value = tripIds;
		form.appendChild(input);

		document.body.appendChild(form);
		form.submit();
		document.body.removeChild(form);
	} else {
		let url = `${base}/documents/loadingList/${locale}/${tripIds}${landscapeOrientation}`;
		window.open(url);
	}
}

async function sendTripToConfirm({}, tripId) {
	try {
		await sendTripRequest(tripId);
	} catch (error) {
		throw new Error('failedToSendTrip');
	}
}

async function updateLoadingSequence({ state, commit }, newSequence) {
	if (!newSequence || !state.tripDetails || !state.tripDetails.orders) return;
	let trip = state.tripDetails;
	
	let original = { orders: [] };
	let newSeq = { orders: [] };
	trip.orders.forEach(order => {
		original.orders.push(order);
		let newOrderData = deepCopy(order);
		newOrderData.loadingSeqNo = newSequence[order.id];
		newSeq.orders.push(newOrderData);
	});
	let patch = rdbClient.createPatch(original, newSeq);

	try {
		let response = await updateTripOrdersRequest(trip.id, { tripPatches: patch, subOrderUpdates: [] });
		if (response.orders)
			commit('setTripOrders', { id: trip.id, orders: response.orders });
		else
			commit('setTripOrders', { id: trip.id, orders: [] });
	} catch (err) {
		throw new Error('failedToChangeLoadingSequence');
	}
}

const deleteTrips = async(_, tripIds) => deleteTripsRequest(tripIds);

export default {
	setValue,
	fetchTrips,
	loadSavedSearch,
	setTripDetails,
	scheduleTripOnEdi,
	removeOrdersFromTrip,
	removeOrdersFromTransporter,
	getLoadingList,
	sendTripToConfirm,
	moveOrderToDifferentTrip,
	updateLoadingSequence,
	deleteTrips,
	...searchFilterActions,
};
